import { useState, useEffect, useRef } from 'react';
import { X, User, Printer, Mail, MessageCircle, Download } from 'lucide-react';

const ACTION_ICONS = {
  print: Printer,
  whatsapp: MessageCircle,
  email: Mail,
  download: Download
};

const ACTION_LABELS = {
  print: 'Print',
  whatsapp: 'Send via WhatsApp',
  email: 'Send via Email',
  download: 'Download'
};

const AddressedPersonModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  actionType = 'print',
  documentType = 'calculation' // 'calculation' or 'quotation'
}) => {
  const [addressedTo, setAddressedTo] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setAddressedTo('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!addressedTo.trim()) {
      setError('Please enter the name of the person this is addressed to');
      return;
    }
    onConfirm(addressedTo.trim());
    setAddressedTo('');
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const Icon = ACTION_ICONS[actionType] || User;
  const actionLabel = ACTION_LABELS[actionType] || 'Share';
  const headerText = documentType === 'quotation' 
    ? 'QUOTATION SUMMARY' 
    : 'TILE CALCULATION';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-lightGray">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-accent-gold" />
            <h3 className="font-semibold text-primary-black">{actionLabel}</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-primary-slate mb-4">
            Enter the name of the person or company this {documentType} is addressed to:
          </p>
          
          {/* Preview */}
          <div className="p-3 bg-secondary-offWhite rounded-lg mb-4 text-center">
            <span className="text-sm text-primary-slate">{headerText} for </span>
            <span className="font-bold text-accent-gold">
              {addressedTo || '[Name]'}
            </span>
          </div>

          {/* Input */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={addressedTo}
              onChange={(e) => {
                setAddressedTo(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter name or company..."
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                error ? 'border-red-500' : 'border-secondary-lightGray'
              }`}
            />
          </div>
          
          {error && (
            <p className="text-red-500 text-sm mt-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-secondary-lightGray bg-secondary-offWhite rounded-b-xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-secondary-lightGray text-primary-slate rounded-lg font-medium hover:bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2.5 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressedPersonModal;
